import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  useGetBotsQuery,
  useCreateBotMutation,
  useUpdateBotMutation,
} from '../store/bots/botsApi';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faSave, faClock, faBriefcase, faCode, faPlus, faTimes, faShoppingCart, faClipboardList } from '@fortawesome/free-solid-svg-icons';
import { GhostSelect } from '../components/ui/GhostSelect';
import { GhostInput } from '../components/ui/GhostInput';
import { GhostSwitch } from '../components/ui/GhostSwitch';
import { useAppSelector } from '../store/hooks';
import { useNotifications } from '../components/ui/notifications';

type Channel = 'instagram' | 'whatsapp' | 'email';

type BotForm = {
  name: string;
  description: string;
  bot_type: string;
  bot_model: string;
  system_prompt: string;
  welcome_message: string;
  fallback_message: string;
  channels: Record<Channel, boolean>;
  is_active: boolean;
  pricing_type: string;
  schedule_mode: 'always' | 'disabled' | 'scheduled';
  schedule: any | null;
  enabled_mcps: string[];
};

function hasInvalidOrOverlap(intervals: Array<{ start: string; end: string }>): boolean {
  if (!Array.isArray(intervals) || intervals.length === 0) return false;
  const toMin = (t: string) => {
    const m = /^([0-2]\d):([0-5]\d)$/.exec(String(t || ''));
    if (!m) return NaN;
    const hh = parseInt(m[1], 10), mm = parseInt(m[2], 10);
    return hh * 60 + mm;
  };
  const arr = intervals
    .map(itv => ({ s: toMin(itv.start), e: toMin(itv.end) }))
    .filter(x => Number.isFinite(x.s) && Number.isFinite(x.e));
  for (const r of arr) if (r.s >= r.e) return true;
  arr.sort((a, b) => a.s - b.s);
  for (let i = 1; i < arr.length; i++) {
    if (arr[i - 1].e > arr[i].s) return true;
  }
  return false;
}

const BotDetailPage: React.FC = () => {
  const { botId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const theme = useAppSelector((state) => state.ui.theme);
  const notifications = useNotifications();

  const isNew = botId === 'new';

  const { data: bots = [], isFetching } = useGetBotsQuery();
  const [createBotMutation] = useCreateBotMutation();
  const [updateBotMutation] = useUpdateBotMutation();

  const defaultForm: BotForm = useMemo(() => ({
    name: '',
    description: '',
    bot_type: 'general',
    bot_model: 'gpt-4o-mini',
    system_prompt: '',
    welcome_message: '¡Hola! 👋 Soy el asistente virtual. ¿En qué puedo ayudarte?',
    fallback_message: 'Disculpa, no pude entender tu mensaje.',
    channels: { instagram: true, whatsapp: false, email: false },
    is_active: false,
    pricing_type: 'base',
    schedule_mode: 'always',
    schedule: null,
    enabled_mcps: []
  }), []);

  const [form, setForm] = useState<BotForm>(defaultForm);
  const [showScheduleJson, setShowScheduleJson] = useState(false);

  useEffect(() => {
    if (!isNew && bots.length > 0) {
      const bot = bots.find(b => b.id === Number(botId));
      if (bot) {
        const channelsRecord: Record<Channel, boolean> = { instagram: false, whatsapp: false, email: false };
        (bot.channels || []).forEach(ch => { if (ch in channelsRecord) channelsRecord[ch as Channel] = true; });

        setForm({
          name: bot.name || '',
          description: bot.description || '',
          bot_type: bot.bot_type || 'general',
          bot_model: bot.bot_model || 'gpt-4o-mini',
          pricing_type: bot.pricing_type || 'base',
          channels: channelsRecord,
          system_prompt: (bot as any).system_prompt || '',
          welcome_message: (bot as any).welcome_message || '',
          fallback_message: (bot as any).fallback_message || '',
          schedule_mode: ((bot as any).schedule_mode as any) || 'always',
          schedule: (bot as any).schedule || null,
          enabled_mcps: bot.enabled_mcps || [],
          is_active: true
        });
      }
    } else if (isNew) {
      setForm(defaultForm);
    }
  }, [botId, isNew, bots, defaultForm]);

  const toggleChannel = (ch: Channel) => {
    if (ch === 'whatsapp' || ch === 'email') return;
    setForm(prev => ({
      ...prev,
      channels: {
        ...prev.channels,
        [ch]: !prev.channels[ch]
      }
    }));
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      notifications.warning('Revisa los datos', t('botsConfigPage.errors.nameRequired'));
      return;
    }

    try {
      const activeChannels = (Object.entries(form.channels) as Array<[Channel, boolean]>)
        .filter(([, v]) => v)
        .map(([k]) => k);

      const payload = {
        name: form.name.trim(),
        description: form.description || '',
        bot_type: form.bot_type,
        bot_model: form.bot_model,
        pricing_type: form.pricing_type || 'base',
        channels: activeChannels.length ? activeChannels : null as any,
        system_prompt: form.system_prompt || null,
        welcome_message: form.welcome_message || null,
        fallback_message: form.fallback_message || null,
        schedule_mode: form.schedule_mode,
        schedule: form.schedule || null,
        enabled_mcps: form.enabled_mcps || [],
        is_active: form.is_active
      };

      if (isNew) {
        await createBotMutation(payload).unwrap();
        notifications.success('Bot creado', `${payload.name} se añadió correctamente.`);
      } else {
        await updateBotMutation({ id: Number(botId), payload }).unwrap();
        notifications.success('Bot actualizado', `${payload.name} se actualizó correctamente.`);
      }
      navigate('/bots-config');
    } catch (e: any) {
      const message = e?.data?.detail || e?.response?.data?.message || e?.message || t('botsConfigPage.errors.save');
      notifications.error('No se pudo guardar el bot', typeof message === 'string' ? message : undefined);
    }
  };

  if (isFetching && !isNew && bots.length === 0) {
    return <div data-ui="bot-detail-loading" className="page-theme-surface page-theme-bots page-theme-blue-white p-8 text-center text-white/50">{t('app.loading')}</div>;
  }

  return (
    <div data-ui="bot-detail-page" className="page-theme-surface page-theme-bots page-theme-blue-white p-4 md:p-6 lg:p-8 max-w-5xl mx-auto flex flex-col gap-6 animate-fade-in">
      <div data-ui="bot-detail-page-region-001" className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div data-ui="bot-detail-page-region-002" className="flex items-center gap-4">
          <button
            onClick={() => navigate('/bots-config')}
            className="btn-taratic btn-ghost-dark flex items-center justify-center w-10 h-10 rounded-full"
          >
            <FontAwesomeIcon icon={faArrowLeft} />
          </button>
          <h1 className="text-2xl md:text-3xl font-bold text-white">
            {isNew ? t('botsConfigPage.create.title') : t('botsConfigPage.bot.editTitle', { name: form.name })}
          </h1>
        </div>
        <div data-ui="bot-detail-page-region-003" className="flex gap-2">
          <button className="btn-taratic btn-ghost-dark flex items-center gap-2" onClick={() => navigate('/bots-config')}>
            <FontAwesomeIcon icon={faTimes} />
            {t('botsConfigPage.buttons.cancel')}
          </button>
          <button className="btn-taratic btn-green flex items-center gap-2" onClick={handleSave}>
            <FontAwesomeIcon icon={faSave} />
            {t('botsConfigPage.buttons.save')}
          </button>
        </div>
      </div>

      <div data-ui="bot-detail-page-region-004" className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm bg-black/20 backdrop-filter backdrop-blur-xl rounded-2xl p-6 shadow-inner-glow shadow-inner-glow-black-md drop-shadow-glow-black-md">
        <label className="grid gap-1">
          <span className="text-sm font-medium text-white/80">{t('botsConfigPage.create.labels.name')}</span>
          <GhostInput value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
        </label>

        <label className="grid gap-1">
          <span className="text-sm font-medium text-white/80">{t('botsConfigPage.create.labels.type')}</span>
          <GhostSelect
            value={form.bot_type}
            onChange={(val: string) => setForm({ ...form, bot_type: val })}
            className="w-full"
            options={[
              { value: "general", label: "general" },
              { value: "ventas", label: "ventas" },
              { value: "soporte", label: "soporte" },
              { value: "reservas", label: "reservas" }
            ]}
          />
        </label>

        <label className="md:col-span-2 grid gap-1">
          <span className="text-sm font-medium text-white/80">{t('botsConfigPage.create.labels.description')}</span>
          <GhostInput value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
        </label>

        <div data-ui="bot-detail-channels" className="md:col-span-2 p-4 bg-black/30 backdrop-filter backdrop-blur-xl rounded-2xl shadow-inner-glow shadow-inner-glow-black-md drop-shadow-glow-black-md flex flex-col gap-3 mt-2">
          <span data-ui="bot-detail-channels-title" className="text-sm font-medium text-white/90">{t('botsConfigPage.create.labels.channels')}:</span>
          <div data-ui="bot-detail-page-region-006" className="flex flex-wrap items-center gap-6">
            {(['instagram', 'whatsapp', 'email'] as Channel[]).map(ch => {
              const isBlocked = ch === 'whatsapp' || ch === 'email';
              return (
                <GhostSwitch
                  key={ch}
                  variant={theme}
                  size="sm"
                  checked={!isBlocked && form.channels[ch]}
                  disabled={isBlocked}
                  onChange={() => !isBlocked && toggleChannel(ch)}
                  label={
                    <span className="uppercase text-xs font-medium text-white/80 flex items-center gap-1.5">
                      {ch}
                      {isBlocked && (
                        <span className="text-[10px] text-amber-400 bg-amber-500/10 border border-amber-500/20 px-1.5 py-0.5 rounded font-normal normal-case">
                          (Bloqueado)
                        </span>
                      )}
                    </span>
                  }
                />
              );
            })}
          </div>
        </div>

        <div data-ui="bot-detail-mcp-capabilities" className="md:col-span-2 text-xs mt-2 p-4 bg-black/30 backdrop-filter backdrop-blur-xl rounded-2xl shadow-inner-glow shadow-inner-glow-black-md drop-shadow-glow-black-md">
          <div data-ui="bot-detail-mcp-capabilities-title" className="font-medium text-white/90 mb-3">Capacidades MCP:</div>
          <div data-ui="bot-detail-page-region-009" className="flex flex-wrap gap-6">
            {[
              { id: 'agendar_citas', label: 'Agendar Citas', icon: faBriefcase },
              { id: 'comprar_productos', label: 'Comprar Productos', icon: faShoppingCart },
              { id: 'pre_ordenes', label: 'Pre-Ordenes', icon: faClipboardList }
            ].map(mcp => {
              const isAvailable = mcp.id === 'agendar_citas';
              const isChecked = form.enabled_mcps.includes(mcp.id);
              return (
                <GhostSwitch
                  key={mcp.id}
                  variant={theme}
                  size="sm"
                  checked={isChecked}
                  disabled={!isAvailable}
                  onChange={(val) => {
                    if (!isAvailable) return;
                    const set = new Set(form.enabled_mcps);
                    if (val) set.add(mcp.id);
                    else set.delete(mcp.id);
                    setForm({ ...form, enabled_mcps: Array.from(set) });
                  }}
                  label={
                    <span className="font-medium text-white/90 text-sm flex items-center gap-1.5">
                      <span>
                        <FontAwesomeIcon icon={mcp.icon} className="mr-1.5" /> {mcp.label}
                      </span>
                      {!isAvailable && (
                        <span className="text-[10px] text-amber-400 bg-amber-500/10 border border-amber-500/20 px-1.5 py-0.5 rounded font-normal normal-case">
                          (Bloqueado)
                        </span>
                      )}
                    </span>
                  }
                />
              );
            })}
          </div>
        </div>

        <div data-ui="bot-detail-page-region-010" className="md:col-span-2 grid gap-1 mt-4">
          <span className="text-sm font-medium text-white/80">{t('botsConfigPage.create.labels.systemPrompt')}</span>
          <textarea
            className="w-full bg-black/20 backdrop-blur-xl border border-white/10 text-white placeholder-white/50 focus:bg-black/75 focus:outline-none focus:ring-1 focus:ring-white/30 focus:border-white/30 rounded-xl py-3 px-4 shadow-sm transition-all duration-300 min-h-[120px]"
            value={form.system_prompt}
            onChange={e => setForm({ ...form, system_prompt: e.target.value })}
          />
          <div data-ui="bot-detail-meta-policy" className="flex items-start gap-2 mt-2 p-3 bg-blue-900/20 border border-blue-500/30 rounded-lg text-blue-200/90 text-xs shadow-inner">
            <i className="fa-solid fa-circle-info text-blue-400 mt-0.5"></i>
            <div data-ui="bot-detail-page-region-012">
              <span className="font-semibold text-blue-300 mb-1 block">Política de Meta: Divulgación de IA</span>
              Asegúrate de instruir al bot en el prompt para que se identifique como una Inteligencia Artificial ante el usuario en su primer mensaje (ej. <em>"Hola, soy el asistente virtual de la clínica..."</em>). Esto previene bloqueos por parte de Meta.
            </div>
          </div>
        </div>

        <label className="grid gap-1 md:col-span-2">
          <span className="text-sm font-medium text-white/80 flex items-center gap-1.5">
            <FontAwesomeIcon icon={faClock} className="text-white/70" />
            {t('botsConfigPage.create.labels.scheduleMode')}
          </span>
          <GhostSelect
            value={form.schedule_mode || 'always'}
            onChange={(val: string) => setForm({ ...form, schedule_mode: val as any })}
            className="w-full"
            options={[
              { value: "always", label: t('botsConfigPage.create.scheduleModes.always') || "Siempre activo" },
              { value: "scheduled", label: t('botsConfigPage.create.scheduleModes.scheduled') || "Programado" },
              { value: "disabled", label: t('botsConfigPage.create.scheduleModes.disabled') || "Desactivado" }
            ]}
          />
        </label>

        {form.schedule_mode === 'scheduled' && (
          <div data-ui="bot-detail-page-region-013" className="md:col-span-2 grid gap-4 mt-4 bg-black/30 backdrop-filter backdrop-blur-xl rounded-2xl shadow-inner-glow shadow-inner-glow-black-md drop-shadow-glow-black-md p-4">
            <div data-ui="bot-detail-page-region-014" className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <span className="text-sm font-medium text-white/80">{t('botsConfigPage.create.labels.weeklySchedule')}</span>
              <div data-ui="bot-detail-page-region-015" className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  className="btn-taratic btn-ghost-dark btn-xs flex items-center gap-1.5"
                  onClick={() => {
                    const all: any = {};
                    ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'].forEach(d => { all[d] = [{ start: '00:00', end: '23:59' }]; });
                    setForm({ ...form, schedule: all });
                  }}
                >
                  <FontAwesomeIcon icon={faClock} />
                  {t('botsConfigPage.create.buttons.preset247')}
                </button>
                <button
                  type="button"
                  className="btn-taratic btn-ghost-dark btn-xs flex items-center gap-1.5"
                  onClick={() => {
                    const sch: any = { mon: [{ start: '09:00', end: '18:00' }], tue: [{ start: '09:00', end: '18:00' }], wed: [{ start: '09:00', end: '18:00' }], thu: [{ start: '09:00', end: '18:00' }], fri: [{ start: '09:00', end: '18:00' }], sat: [], sun: [] };
                    setForm({ ...form, schedule: sch });
                  }}
                >
                  <FontAwesomeIcon icon={faBriefcase} />
                  {t('botsConfigPage.create.buttons.presetOffice')}
                </button>
                <button
                  type="button"
                  className="btn-taratic btn-ghost-dark btn-xs flex items-center gap-1.5"
                  onClick={() => setShowScheduleJson(!showScheduleJson)}
                >
                  <FontAwesomeIcon icon={faCode} />
                  {showScheduleJson ? t('botsConfigPage.create.buttons.editorSimple') : t('botsConfigPage.create.buttons.editorAdvanced')}
                </button>
              </div>
            </div>

            {showScheduleJson ? (
              <label className="grid gap-1 mt-2">
                <span className="text-sm font-medium text-white/80">{t('botsConfigPage.create.labels.jsonSchedule')}</span>
                <textarea
                  className="w-full bg-black/20 backdrop-blur-xl border border-white/10 text-white placeholder-white/50 focus:bg-black/75 focus:outline-none focus:ring-1 focus:ring-white/30 focus:border-white/30 rounded-xl py-2 px-4 shadow-sm transition-all duration-300 min-h-[160px] font-mono text-sm"
                  placeholder={`{"mon":[{"start":"09:00","end":"18:00"}], "tue":[...], ...}`}
                  value={typeof form.schedule === 'string' ? form.schedule : JSON.stringify(form.schedule || {}, null, 2)}
                  onChange={e => {
                    try {
                      const parsed = JSON.parse(e.target.value);
                      setForm({ ...form, schedule: parsed });
                    } catch {
                      setForm({ ...form, schedule: e.target.value });
                    }
                  }}
                />
                <span className="text-[11px] text-white/60">{t('botsConfigPage.create.labels.jsonFormatHint')}</span>
              </label>
            ) : (
              <div data-ui="bot-detail-page-region-016" className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
                {(['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const).map((dayKey) => {
                  const dayLabels: Record<string, string> = {
                    mon: t('schedulesPage.regulars.days.monday'),
                    tue: t('schedulesPage.regulars.days.tuesday'),
                    wed: t('schedulesPage.regulars.days.wednesday'),
                    thu: t('schedulesPage.regulars.days.thursday'),
                    fri: t('schedulesPage.regulars.days.friday'),
                    sat: t('schedulesPage.regulars.days.saturday'),
                    sun: t('schedulesPage.regulars.days.sunday'),
                  };
                  const scheduleObj: any = form.schedule || {};
                  const intervals: Array<{ start: string; end: string }> = Array.isArray(scheduleObj[dayKey]) ? scheduleObj[dayKey] : [];
                  const enabled = intervals.length > 0;

                  const setSchedule = (next: any) => setForm({ ...form, schedule: next });

                  return (
                    <div data-ui="bot-detail-page-region-017" key={dayKey} className="rounded-2xl p-3 bg-black/20 backdrop-filter backdrop-blur-xl shadow-inner-glow shadow-inner-glow-black-md drop-shadow-glow-black-md">
                      <div data-ui="bot-detail-page-region-018" className="flex items-center justify-between">
                        <GhostSwitch
                          variant={theme}
                          size="sm"
                          checked={enabled}
                          onChange={(checked) => {
                            const next = { ...(scheduleObj || {}) };
                            if (checked) {
                              next[dayKey] = [{ start: '09:00', end: '18:00' }];
                            } else {
                              next[dayKey] = [];
                            }
                            setSchedule(next);
                          }}
                          label={
                            <span className="text-sm font-medium text-white/90">
                              {dayLabels[dayKey]}
                            </span>
                          }
                        />
                        <button
                          type="button"
                          className="btn-taratic btn-ghost-dark btn-xs flex items-center gap-1.5"
                          onClick={() => {
                            const next = { ...(scheduleObj || {}) };
                            next[dayKey] = [...(intervals || []), { start: '09:00', end: '18:00' }];
                            setSchedule(next);
                          }}
                          disabled={!enabled}
                        >
                          <FontAwesomeIcon icon={faPlus} />
                          {t('botsConfigPage.create.buttons.addInterval')}
                        </button>
                      </div>

                      {enabled && intervals.map((itv, idx) => (
                        <div data-ui="bot-detail-page-region-019" key={idx} className="mt-3 flex items-center gap-2">
                          <input
                            type="time"
                            className="bg-black/20 border border-white/10 rounded px-2 py-1 text-sm text-white focus:outline-none focus:border-white/30"
                            value={itv.start || ''}
                            onChange={(e) => {
                              const next = { ...(scheduleObj || {}) };
                              const list = [...(intervals || [])];
                              list[idx] = { ...list[idx], start: e.target.value };
                              next[dayKey] = list;
                              setSchedule(next);
                            }}
                          />
                          <span className="text-sm text-white/60">→</span>
                          <input
                            type="time"
                            className="bg-black/20 border border-white/10 rounded px-2 py-1 text-sm text-white focus:outline-none focus:border-white/30"
                            value={itv.end || ''}
                            onChange={(e) => {
                              const next = { ...(scheduleObj || {}) };
                              const list = [...(intervals || [])];
                              list[idx] = { ...list[idx], end: e.target.value };
                              next[dayKey] = list;
                              setSchedule(next);
                            }}
                          />
                          <button
                            type="button"
                            className="btn-taratic btn-red btn-xs flex items-center justify-center rounded px-2 py-1"
                            onClick={() => {
                              const next = { ...(scheduleObj || {}) };
                              const list = [...(intervals || [])];
                              list.splice(idx, 1);
                              next[dayKey] = list;
                              setSchedule(next);
                            }}
                          >
                            <FontAwesomeIcon icon={faTimes} />
                          </button>
                        </div>
                      ))}
                      {enabled && hasInvalidOrOverlap(intervals) && (
                        <div data-ui="bot-detail-page-region-020" className="mt-2 text-xs text-red-500 font-medium">{t('botsConfigPage.schedule.overlapError')}</div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default BotDetailPage;
